import { Outlet } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Sidebar } from '../../components/Sidebar'
import { AccountMenu } from '../../components/AccountMenu'
import { AppShellLayout } from '../../components/AppShellLayout'
import { ShellHeader } from '../../components/ShellHeader'
import { OrgContextSwitcher } from '../../components/OrgContextSwitcher'
import { walletSidebarItems } from '../../lib/walletSidebar'
import { workspaceSidebarItems } from '../../lib/workspaceSidebar'
import { useTaskBadge } from '../staff-tasks/useTaskBadge'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { useT } from '../../i18n'
import { adminShellI18n } from '../../i18n/admin/shell'
import { drivePersonalI18n } from '../../i18n/shared/drive-personal'
import { groupingNavI18n } from '../../i18n/admin/grouping'
import { groupingCount } from '../../api/grouping'
import { messagingI18n } from '../../i18n/messaging'
import { useMessagingCapabilities } from '../shared/messaging/useMessagingCapabilities'
import { useMessagingUnread } from '../shared/messaging/useMessagingUnread'
import { useMessagingRealtime } from '../shared/messaging/useMessagingRealtime'
import { AdminHelpLauncher } from './AdminHelpLauncher'
import { AdminHubTabs } from './admin-hubs'
import { hubMenuEntry } from './admin-hubs-config'
import { adminHubsI18n } from '../../i18n/admin/hubs'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import {
  buildAdminStoreItems,
  type AdminAwaitingShipmentStore,
} from './awaiting-shipment-stores'
import { fetchMe } from '../../lib/auth'
import { useRemoteAccessAvailability } from './servers/useRemoteAccess'

// Sidebar IA refactor (Faz A 2026-05-24 — UI/UX Pro Max audit):
// Mental model: Volkan A1 daily workflow → Overview · Orders · Catalog · Inventory ·
// Queues · Activity · Finance & Wallet · Workspace · System.
//
// Simplification 2026-10-05 (plan: one job in one place; set-and-forget config
// in Settings; daily work in the main menu): duplicate links removed, every
// work list gathered under Queues, one-item groups flattened, config screens
// moved to Settings. Every route still resolves — only its menu place changed.
//
// 21 flat entry → 9 default-visible (collapsible parents auto-expand on active child).
// Hick's Law: choice time ∝ log₂(N+1); -57% visible items at rest.
//
// Discipline (memory feedback_no_developer_tool_ui + operational simulation):
// - Internal ceremony tags (IQ-1/IQ-2/IQ-7/Q-018) stripped from labels
// - Demo Guide moved to bottom system section (not daily ops)
// - Schema-name leaks ("Inventory · Products" prefix-encoding) replaced with proper groups
//
// D-7 STRICT surface segregation: admin shell only; seller/supply untouched.

export function AdminShell() {
  const t = useT(adminShellI18n)
  const tNav = useT(groupingNavI18n)
  const td = useT(drivePersonalI18n)
  const th = useT(adminHubsI18n)
  const tm = useT(messagingI18n)
  const tTasks = useT(staffTasksI18n)
  const taskBadge = useTaskBadge()
  const messagingCapabilities = useMessagingCapabilities()
  const remoteAccess = useRemoteAccessAvailability()
  const messagingUnread = useMessagingUnread(
    'admin',
    messagingCapabilities.can_view_inbox,
  )
  useMessagingRealtime('admin', messagingCapabilities.can_view_inbox)
  // Shared count for both Ungrouped Products nav entries (Inventory + Queues
  // shortcut). One query, one number — ShipStation-style right-aligned badge.
  const groupingCountQuery = useQuery({
    queryKey: ['admin-grouping-count'],
    queryFn: groupingCount,
  })
  const ungroupedCount = groupingCountQuery.data?.stub_count ?? 0
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  // Exit plan is the platform owner's own page: shown only when /auth/me carries
  // exit_plan.view (never in mock mode — the API would refuse it anyway).
  const canViewExitPlan =
    USE_REAL_API && Boolean(meQuery.data?.capabilities.includes('exit_plan.view'))
  const canViewSalesGoals =
    !USE_REAL_API || Boolean(meQuery.data?.capabilities.includes('sales_goal.view'))
  // Leads (mig 391) are real-API only and need leads.use; without it the
  // Seller Applications entry opens on Invitations and the tab stays hidden.
  const canUseLeads =
    USE_REAL_API && Boolean(meQuery.data?.capabilities.includes('leads.use'))
  const hiddenHubTabs = [
    ...(canViewSalesGoals ? [] : ['/admin/sales-goals']),
    ...(canUseLeads ? [] : ['/admin/leads', '/admin/leads/scoreboard']),
  ]
  // Store breakdown under Awaiting Shipment. Same cache prefix as the order
  // list, so any order mutation that invalidates ['admin','orders'] refreshes
  // these counts too — they can never drift from the list they open.
  const storeCounts = useQuery({
    queryKey: ['admin', 'orders', 'awaiting-shipment-stores'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/admin/orders/awaiting-shipment-stores'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()) as { items: AdminAwaitingShipmentStore[] }
    },
    enabled: USE_REAL_API,
  })
  const awaitingShipmentStores = buildAdminStoreItems(storeCounts.data?.items ?? [])
  // Finance group head first, then Fees & charges + Wallet Directory, then the
  // rest of the wallet entries (Bank Approvals, Partner Settlements).
  const financeItems = walletSidebarItems('/admin', t.items.finance)
  return (
    <AppShellLayout
      sidebar={
        <Sidebar
          title={t.title}
          layout="aligned"
          header={<ShellHeader selector={<OrgContextSwitcher />} />}
          headerIcon="organization"
          items={[
          // ─── Overview (direct top-level) ─────────────────────────────────
          { to: '/admin', label: t.items.overview, icon: 'navOverview' },
          ...(messagingCapabilities.can_view_inbox
            ? [{
                to: '/admin/inbox',
                label: tm.navigation.admin,
                icon: 'messages' as const,
                badge: messagingUnread.unreadMessages,
              }]
            : []),

          // ═══ Günlük — daily high-frequency operations ════════════════════
          { to: '/admin/_band_daily', label: t.bands.daily, isHeader: true },

          // Orders — expandable group (ShipStation-parity hierarchical nav).
          // Sub-items use ?status= URL param matching live QuickFilter set.
          {
            to: '/admin/_group_orders',
            label: t.items.orders,
            icon: 'navOrders',
            groupId: 'admin-orders',
          },
          // Awaiting Shipment is the daily default → empty URL.
          // All Orders requires explicit ?status=all so Volkan's habit stays.
          {
            to: '/admin/orders',
            label: t.items.awaitingShipment,
            icon: 'products',
            parentGroupId: 'admin-orders',
            // ShipStation-parity store breakdown: every connected shop with
            // work waiting, so labels can be printed store by store.
            subItems: awaitingShipmentStores,
          },
          {
            to: '/admin/orders?status=all',
            label: t.items.allOrders,
            icon: 'layers',
            parentGroupId: 'admin-orders',
          },
          {
            to: '/admin/orders?status=awaiting_seller_approval',
            label: t.items.awaitingApproval,
            icon: 'monetization',
            parentGroupId: 'admin-orders',
          },
          {
            to: '/admin/orders?status=on_hold',
            label: t.items.onHold,
            icon: 'pause',
            parentGroupId: 'admin-orders',
          },
          {
            to: '/admin/orders?status=shipped',
            label: t.items.shipped,
            icon: 'fulfillment',
            parentGroupId: 'admin-orders',
          },
          // Returns opens the processing queue rather than the "Returned"
          // order filter: the queue is where a return is actually worked, and
          // the filter stays one click away on the order list (2026-10-05).
          {
            to: '/admin/returns',
            label: t.items.returns,
            icon: 'packageUnavailable',
            parentGroupId: 'admin-orders',
          },

          // ─── Catalog — master library governance ─────────────────────────
          {
            to: '/admin/_group_catalog',
            label: t.groups.catalog,
            icon: 'navCatalog',
            groupId: 'admin-catalog',
          },
          {
            to: '/admin/catalog/products',
            label: t.items.products,
            icon: 'products',
            parentGroupId: 'admin-catalog',
          },
          {
            to: '/admin/catalog/bundles',
            label: t.items.bundles,
            icon: 'bundles',
            parentGroupId: 'admin-catalog',
          },
          {
            to: '/admin/catalog/categories',
            label: t.items.categories,
            icon: 'layers',
            parentGroupId: 'admin-catalog',
          },
          // Pricing left Catalog for its own entry under Manage, and its fees
          // tab moved to Finance → Fees & charges (Volkan 2026-10-26).
          // Pricing Matrix is reached as a tab inside Pricing Hub, not as a nav
          // entry of its own — the catalogue-wide cost grid belongs beside the
          // rules that price it. The route still resolves for old deep links
          // (Volkan, 2026-09-05).
          // Product Normalization is intentionally NOT in the nav: the CSV bulk
          // "nest flat seed products under one Master Product" tool belongs to
          // the flat-seed era, and today the same work is done on-screen (SKU
          // merge, cross-product merge, map-into-options). Route, API and i18n
          // stay in place — restore the entry if a large messy import lands
          // (Volkan, 2026-09-05).
          // Marketplace Listings and SKU aliases are tabs of SKU Mapping under
          // Queues; Girgin Import is a button on the Products page (2026-10-05).
          // Supplier Catalog (Printify / JDS / Gelato curation) came in from
          // Settings — it is daily catalogue work.
          {
            to: '/admin/catalog/suppliers',
            label: th.supplierCatalog,
            icon: 'factory',
            parentGroupId: 'admin-catalog',
          },
          {
            to: '/admin/assortment',
            label: t.items.assortment,
            icon: 'catalog',
            parentGroupId: 'admin-catalog',
          },
          // B2B Push is intentionally NOT in the nav: the wholesale channel has
          // no real BO3 buyer yet, so the package desk would be an empty ledger.
          // Routes, API and i18n stay in place — restore the entry when the
          // channel opens (Volkan, 2026-09-05).
          // Sample requests (Sampler port) — lives under the Catalogue group.
          {
            to: '/admin/samples',
            label: t.items.samples,
            icon: 'experiment',
            parentGroupId: 'admin-catalog',
          },
          // Pexify Jewelry Fonts is the only Design Studio screen with a real
          // backend, so live it sits with the catalogue instead of heading a
          // one-item group (2026-10-05).
          {
            to: '/admin/personalize/jewelry-fonts',
            label: t.items.mtsFonts,
            icon: 'typography',
            parentGroupId: 'admin-catalog',
          },

          // ─── Inventory operations ────────────────────────────────────────
          // The group head is the dashboard: clicking "Inventory" expands the
          // submenu AND lands on /admin/inventory, so the separate "Dashboard"
          // child that used to sit here is redundant (Volkan, 2026-09-06).
          {
            to: '/admin/inventory',
            label: t.groups.inventory,
            icon: 'navInventory',
            groupId: 'admin-inventory',
          },
          {
            to: '/admin/inventory/products',
            label: t.items.stock,
            icon: 'products',
            parentGroupId: 'admin-inventory',
          },
          {
            to: '/admin/inventory/movements',
            label: t.items.movements,
            icon: 'time',
            parentGroupId: 'admin-inventory',
          },
          {
            to: '/admin/inventory/valuation',
            label: t.items.stockValue,
            icon: 'wallet',
            parentGroupId: 'admin-inventory',
          },
          // Hub Receiving is intentionally NOT in the nav: scanning a master
          // shipment into a hub is a multi-node custody flow, and PEXIFY runs a
          // single NJ warehouse — nothing arrives that way today. Route, API and
          // component stay in place (Volkan, 2026-09-06).
          // Purchase Orders folded under Inventory (was its own "Procurement"
          // group; collapsed 2026-05-30 — operator mental model "stock low →
          // PO" couples the two surfaces. Create flow lives on PO List page
          // via "+ New PO" button rather than duplicating a sidebar entry.
          // V1.5+ reactivates Procurement group if disputes / reorder /
          // vendor reports add 3+ entries.)
          {
            to: '/admin/po',
            label: t.items.purchaseOrders,
            icon: 'documents',
            parentGroupId: 'admin-inventory',
          },
          {
            to: '/admin/inventory/import',
            label: t.items.stockImport,
            icon: 'upload',
            parentGroupId: 'admin-inventory',
          },

          // ─── Queues — cross-domain operational backlog ───────────────────
          // Font Approvals moved to Design Studio domain (Volkan 2026-05-24:
          // "font approvals design studio'nun altına gitmez mi" — domain
          // coupling > generic queue inbox).
          {
            to: '/admin/_group_queues',
            label: t.groups.queues,
            icon: 'inbox',
            groupId: 'admin-queues',
          },
          // One entry per job, the sibling screens as route tabs
          // (2026-10-05): SKU Mapping = listing triage (when a store connects)
          // + unmapped SKUs (when an order arrives) + mapped listings +
          // alternate codes; Catalog Corrections = supplier suggestions +
          // seller reports + vendor replacements.
          {
            ...hubMenuEntry('skuMapping'),
            label: th.skuMapping.title,
            icon: 'store',
            parentGroupId: 'admin-queues',
          },
          {
            ...hubMenuEntry('catalogCorrections'),
            label: th.catalogCorrections.title,
            icon: 'warning',
            parentGroupId: 'admin-queues',
          },
          // Ungrouped Products lives here only — it used to be listed under
          // Inventory as well, two links to one worklist (2026-10-05).
          {
            to: '/admin/inventory/ungrouped',
            label: tNav.ungroupedProducts,
            icon: 'fileSearch',
            parentGroupId: 'admin-queues',
            badge: ungroupedCount,
          },
          // Work lists that only exist to be emptied belong with the other
          // queues: parcels still to measure, oversold lines, orders shipped
          // outside PEXIFY, and made-to-order jobs to place with producers.
          // Production was a one-item group (2026-10-05).
          {
            to: '/admin/catalog/measurement-queue',
            label: t.items.measurementQueue,
            icon: 'products',
            parentGroupId: 'admin-queues',
          },
          {
            to: '/admin/inventory/backorders',
            label: t.items.backorders,
            icon: 'warning',
            parentGroupId: 'admin-queues',
          },
          {
            to: '/admin/inventory/external-fulfillment',
            label: t.items.externalFulfillment,
            icon: 'packageUnavailable',
            parentGroupId: 'admin-queues',
          },
          {
            to: '/admin/production/producer-queue',
            label: t.items.producerQueue,
            icon: 'factory',
            parentGroupId: 'admin-queues',
          },


          // ═══ Yönet — periodic management + governance ════════════════════
          { to: '/admin/_band_manage', label: t.bands.manage, isHeader: true },

          ...(remoteAccess.state === 'available'
            ? [{
                to: '/admin/servers',
                label: t.items.servers,
                icon: 'servers' as const,
              }]
            : []),

          // ─── Pricing (product price per tier) — its own entry since
          // 2026-10-26 (Volkan): a catalogue-wide policy, not a Catalog page.
          {
            to: '/admin/pricing',
            alsoActiveFor: ['/admin/catalog/pricing-hub', '/admin/catalog/pricing-matrix'],
            label: th.pricing.title,
            icon: 'monetization',
          },

          // Stores and Sales Goals are two tabs of one entry (2026-10-06);
          // the Sales Goals tab shows only with `sales_goal.view`.
          {
            ...hubMenuEntry('stores'),
            label: th.stores.title,
            icon: 'store' as const,
          },

          // ─── Reviews (Etsy reviews + recovery tracking) ──────────────────
          // Per-store tracking on/off is set-and-forget config and moved to
          // Settings → Review Tracking, so Reviews is one entry (2026-10-05).
          { to: '/admin/reviews', label: t.groups.reviews, icon: 'favorite' },

          // ─── Reports (D30 BE-204/205 governance roll-ups — A1 Ring 3) ────
          { to: '/admin/reports', label: t.items.reports, icon: 'fileSearch' },
          ...(canViewExitPlan
            ? [{ to: '/admin/exit-plan', label: t.items.exitPlan, icon: 'planning' as const }]
            : []),

          // ─── Activity (audit trail) ──────────────────────────────────────
          { to: '/admin/activity', label: t.items.activity, icon: 'activity' },

          // ─── Finance (collapsible submenu: fees, wallets, settlements, Bank)
          // Fees & charges moved in from Pricing (Volkan 2026-10-26): what a
          // seller is charged sits with the wallets it is taken from.
          ...financeItems.slice(0, 1),
          {
            to: '/admin/finance/fees',
            alsoActiveFor: ['/admin/monetization'],
            label: t.items.feesAndCharges,
            icon: 'monetization',
            parentGroupId: 'wallet-admin',
          },
          {
            to: '/admin/finance/subscriptions',
            label: t.items.subscriptions,
            icon: 'receipt',
            parentGroupId: 'wallet-admin',
          },
          {
            to: '/admin/wallets',
            label: t.items.walletDirectory,
            icon: 'wallet',
            parentGroupId: 'wallet-admin',
          },
          {
            to: '/admin/finance/incoming-wires',
            label: t.items.incomingWires,
            icon: 'banking',
            parentGroupId: 'wallet-admin',
          },
          ...financeItems.slice(1),
          // Bank is PEXIFY's own Wise-backed corporate cash — still distinct
          // from the two admin "wallet" surfaces above, but it is money we hold,
          // so it belongs inside the finance group rather than beside it
          // (Volkan, 2026-09-06).
          {
            to: '/admin/bank',
            label: t.items.bank,
            icon: 'banking',
            parentGroupId: 'wallet-admin',
          },
          // Policies (agreement texts) moved to Settings → Policies: written
          // once, versioned rarely — configuration, not a daily screen
          // (2026-10-05).

          // ─── Drive — unified file-manager (home) + admin governance tools ────
          // Drive home is the personal/team file-manager; Explorer/Triage are
          // the admin oversight tools. Maintenance (the storage clean-up run)
          // moved to Settings → System (2026-10-05).
          {
            to: '/admin/_group_drive',
            label: t.groups.drive,
            icon: 'cloud',
            groupId: 'admin-drive',
          },
          {
            to: '/admin/drive',
            label: td.pageTitle,
            icon: 'cloud',
            parentGroupId: 'admin-drive',
          },
          // All Files: the folder-tree Explorer and the file-activity log
          // (formerly Settings → Drive Audit) as two tabs (2026-10-05).
          {
            ...hubMenuEntry('allFiles'),
            label: th.allFiles.title,
            icon: 'organization',
            parentGroupId: 'admin-drive',
          },
          {
            to: '/admin/drive-triage',
            label: t.items.driveTriage,
            icon: 'inbox',
            parentGroupId: 'admin-drive',
          },

          // ─── Sellers — partner onboarding + inbound applications ─────────
          // Company Registrations (Start-a-U.S.-company applications) is the
          // same "someone wants in" pipeline, so it joined this group
          // (2026-10-05).
          {
            to: '/admin/_group_sellers',
            label: t.groups.sellers,
            icon: 'users',
            groupId: 'admin-sellers',
          },
          // Invitations and inbound applications are two tabs of one entry —
          // an approved application turns into an invitation (2026-10-05).
          {
            ...(canUseLeads
              ? hubMenuEntry('sellerApplications')
              : { to: '/admin/onboarding', alsoActiveFor: ['/admin/self-apply'] }),
            label: th.sellerApplications.title,
            icon: 'email',
            parentGroupId: 'admin-sellers',
          },
          {
            to: '/admin/company-registrations',
            label: t.items.companyRegistrations,
            icon: 'organization',
            parentGroupId: 'admin-sellers',
          },

          // ═══ Tools — catalogue publishing + utilities ═════════════════════
          { to: '/admin/_band_setup', label: t.bands.setup, isHeader: true },

          // ─── Catalog PDF (Document Studio) ──────────────────────────────
          // New catalog + generated files are two tabs of one entry. Brand
          // Pages left the nav and Brand Settings lives in Settings → Catalog
          // Brand (2026-10-05).
          {
            ...hubMenuEntry('catalogPdf'),
            label: th.catalogPdf.title,
            icon: 'documents',
          },

          // Etsy profit calculator — was the only item of a "Tools" group.
          {
            to: '/admin/tools/etsy-profit',
            label: t.items.etsyProfitCalc,
            icon: 'calculator',
          },
          // Etsy research on Alura data — PEXIFY team only (Alura API Terms §5).
          {
            to: '/admin/tools/etsy-research/keyword',
            label: t.items.etsyResearch,
            icon: 'fileSearch',
          },
          // Etsy Trends — nightly niche measurements; Manage tab owns the lists.
          {
            to: '/admin/tools/etsy-trends',
            label: t.items.etsyTrends,
            icon: 'insights',
          },
          // US event calendar — US days that move sales; Manage tab owns the list.
          {
            to: '/admin/tools/us-calendar',
            label: t.items.usCalendar,
            icon: 'calendar',
          },

          // ─── Design Studio (simulation only) ─────────────────────────────
          // Live, its one real screen (Pexify Jewelry Fonts) sits under
          // Catalog. The other 5 post to routes the real backend does not have
          // (verified against shared/api/openapi-backend.yaml), so the group
          // exists only in the simulation, where the mock server implements
          // them.
          ...(USE_REAL_API
            ? []
            : [
                {
                  to: '/admin/_group_design_studio',
                  label: t.groups.designStudio,
                  icon: 'brand' as const,
                  groupId: 'admin-design-studio',
                },
                {
                  to: '/admin/personalize',
                  label: t.items.designStudioOverview,
                  icon: 'dashboard' as const,
                  parentGroupId: 'admin-design-studio',
                },
                {
                  to: '/admin/personalize/categories',
                  label: t.items.designStudioCategories,
                  icon: 'layers' as const,
                  parentGroupId: 'admin-design-studio',
                },
                {
                  to: '/admin/personalize/fonts',
                  label: t.items.fonts,
                  icon: 'typography' as const,
                  parentGroupId: 'admin-design-studio',
                },
                {
                  to: '/admin/personalize/fonts/approval',
                  label: t.items.approvalQueue,
                  icon: 'security' as const,
                  parentGroupId: 'admin-design-studio',
                },
                {
                  to: '/admin/personalize/composer',
                  label: t.items.composerPreview,
                  icon: 'preview' as const,
                  parentGroupId: 'admin-design-studio',
                },
              ]),

          // ─── Workspace (existing header + 2 items pattern) ───────────────
          ...workspaceSidebarItems('/admin', { label: tTasks.navLabel, badge: taskBadge }),
          // Trainings — how-to articles for the team (mig 388).
          { to: '/admin/trainings', label: t.items.trainings, icon: 'learning' },

          // ─── Settings (full-page shell — gear, alone at the bottom) ──────
          // All settings/configuration surfaces live in the Settings page
          // (AdminSettingsShell), including Policies, Payment Gateways, Review
          // Tracking, Catalog Brand and Drive Maintenance (moved 2026-10-05).
          { to: '/admin/_settings_divider', label: '', isHeader: true },
          { to: '/admin/settings', label: t.items.settings, icon: 'navSettings' },
          ]}
          footer={<AccountMenu shellBase="/admin" fallbackName="Admin (demo)" />}
        />
      }
    >
      <AdminHubTabs hiddenTabs={hiddenHubTabs} />
      <Outlet />
      <AdminHelpLauncher
        canChat={messagingCapabilities.can_view_inbox}
        unreadCount={messagingUnread.unreadMessages}
      />
    </AppShellLayout>
  )
}
